import * as React from 'react';
import {
  Box,
  FormControl,
  MenuItem,
  Pagination,
  PaginationItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import type { MRT_TableInstance as MRTTableInstance } from 'material-react-table';
import { useTheme } from '@mui/material/styles';

type Props<TData extends Record<string, any>> = {
  table: MRTTableInstance<TData>;
  pageSizeOptions?: number[];
};

export const TablePaginationBar = <TData extends Record<string, any>>({
  table,
  pageSizeOptions = [10, 25, 50, 100],
}: Props<TData>) => {
  const theme = useTheme();
  const activeColor = theme.custom.pagination.active;

  const { pageIndex, pageSize } = table.getState().pagination;
  const pageCount = table.getPageCount();

  const handlePageSizeChange = (e: SelectChangeEvent<number>) => {
    const next = Number(e.target.value);
    table.setPageSize(next);
    table.setPageIndex(0);
  };

  if (pageCount <= 1 && !pageSizeOptions.length) return null;

  return (
    <Box
      sx={{
        px: 2,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        flexWrap: 'wrap',
      }}
    >
      {/* Rows per page */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2">Rows per page</Typography>
        <FormControl size="small">
          <Select<number> value={pageSize} onChange={handlePageSizeChange} sx={{ minWidth: 90 }}>
            {pageSizeOptions.map(opt => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Numbers */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Pagination
          page={pageIndex + 1}
          count={pageCount}
          onChange={(_, value) => table.setPageIndex(value - 1)}
          renderItem={item => (
            <PaginationItem
              {...item}
              sx={{
                minWidth: 42,
                height: 42,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 1,
                fontWeight: 600,

                '&.Mui-selected': {
                  backgroundColor: activeColor,
                  borderColor: activeColor,
                  color: '#fff',
                  '&:hover': { backgroundColor: activeColor },
                },

                '&.Mui-disabled': {
                  backgroundColor: 'grey.100',
                  color: 'grey.400',
                  boxShadow: 'none',
                },
              }}
            />
          )}
        />
      </Box>
    </Box>
  );
};
